import { BookingPagingResponse, BookingPagingResponseBasePagingResponse } from '@/__generated';
import NtaDataGrid, { NTADataGridColumnProps } from '@/components/NtaDataGrid/NtaDataGrid';
import Spin from '@/components/Spin';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { apiSdk } from '@/httpClient';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { UNKNOWN_ERROR_MESSAGE, appDisplayDateFormat } from '@/config';
import NtaTag from '@/components/NtaTag';
import { BookingStatus, PaymentStatus } from '@/constants';
import { NTACurrencyFormat } from '@/hooks/useCurrencyFormat';
import SearchForm from './SearchForm';
import EmptyRowsRenderer from '@/components/NtaDataGrid/EmptyRowsRenderer';
import { AppUrls } from '@/urls';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCancel, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Link, useHistory } from 'react-router-dom';
import CancelBookingModal from '@/components/App/BookingComponents/CancelBookingModal';
import bookingService from '@/services/bookingService';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { isNumber } from 'lodash';

const DEFAULT_PAGE_SIZE = 20;
interface SearchParams {
  bookingStatus?: any;
  paymentStatus?: any;
  /**  */
  pageIndex?: number;
  /**  */
  pageSize?: number;
  /**  */
  orderBy?: string;
  /**  */
  orderByAsc?: boolean;
}

const getInitSearchValue = () => {
  let otherParams = {};
  const query = new URLSearchParams(window.location.search);
  const autoCancelTimeString = query.get('autoCancelTime');
  let autoCancelTime = parseInt(autoCancelTimeString || '');
  if (autoCancelTime && isNumber(autoCancelTime)) {
    otherParams = {
      autoCancelTime: autoCancelTime,
    };
  }
  return otherParams;
};
export default function AgentOnHoldBookingsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [bookingResponse, setBookingResponse] = React.useState<BookingPagingResponseBasePagingResponse>();
  const [bookingCancelled, setBookingCancelled] = React.useState<BookingPagingResponse>();
  const history = useHistory();
  const [searchParams, setSearchParams] = React.useState<SearchParams>({
    pageSize: DEFAULT_PAGE_SIZE,
    pageIndex: 1,
    orderBy: 'refNumber',
    orderByAsc: false,
  });
  const { isAdmin, isBooking } = useGetUseRole();
  if (!isAdmin && !isBooking) {
    history.push(AppUrls.Page401);
    return null;
  }

  const getData = () => {
    setLoading(true);
    apiSdk.AgentBookingService.getBookings({
      body: {
        ...getInitSearchValue(),
        ...searchParams,
        isOnHold: true,
        bookingStatus: [BookingStatus.Hold, BookingStatus.PartialHold, BookingStatus.Processing] as any,
      },
    })
      .then(res => {
        setBookingResponse(res);
      })
      .catch(() => {
        ModalUtils.error({ content: UNKNOWN_ERROR_MESSAGE });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    getData();
  }, [searchParams]);

  const cancelBooking = (booking: BookingPagingResponse) => {
    bookingService
      .handleOnCancelBooking(
        booking.roomStatus?.map((status, index) => ({
          checkInDate: booking?.checkinDate,
          supplier: booking?.roomSuppliers?.at(index) as any,
          status: status as any,
        })) || [],
      )
      .then(next => {
        if (next) {
          setBookingCancelled(booking);
        }
      });
  };
  const onSearch = values => {
    history.replace('/agent/on-hold-bookings');

    setSearchParams({
      pageSize: DEFAULT_PAGE_SIZE,
      pageIndex: 1,
      orderBy: 'refNumber',
      orderByAsc: false,
      ...values,
      autoCancelTime: values.autoCancelTime?.value,
    });
  };

  const onPageChange = (selectedItem: { selected: number }) => {
    setSearchParams(s => ({
      ...s,
      pageIndex: selectedItem.selected + 1,
    }));
  };
  const columns = React.useMemo(() => {
    return [
      {
        key: 'refNumber',
        name: <Trans>agentBookingListPage_RefNumber</Trans>,
        width: 180,
        frozen: true,
        renderCell: ({ row }) => (
          <a target="_blank" rel="noreferrer" className="text-blue-1" href={AppUrls.__createBookingDetailUrl(row.id || '')}>
            <b>{row.refNumber}</b>
          </a>
        ),
      },
      {
        key: 'orderId',
        name: <Trans>agentBookingListPage_OrderId</Trans>,
        width: 160,
        frozen: true,
        renderCell: ({ row }) => (
          <a target="_blank" rel="noreferrer" className="text-blue-1" href={AppUrls.__createAgentOrderDetailUrl(row.orderId || '')}>
            <b>{row.orderId}</b>
          </a>
        ),
      },
      {
        key: 'supplierRefNumbers',
        name: <Trans>agentBookingListPage_SupplierRefNumbers</Trans>,
        width: 150,
        maxWidth: 200,
        renderCell: ({ row }) => (
          <span className="text-truncate" title={row.supplierRefNumbers?.join(',')}>
            {row.supplierRefNumbers?.join(',')}
          </span>
        ),
      },
      {
        key: 'hotelName',
        name: <Trans>agentBookingListPage_HotelName</Trans>,
        minWidth: 200,

        renderCell: ({ row }) => (
          <span className="text-truncate" title={row.hotelName}>
            {row.hotelName}
          </span>
        ),
      },
      {
        key: 'bookingStatus',
        name: <Trans>agentBookingListPage_BookingStatus</Trans>,
        width: 150,
        renderCell: ({ row }) => <NtaTag color={BookingStatus.__getColor(row.bookingStatus)} text={BookingStatus.__getValue(row.bookingStatus)} />,
      },
      {
        key: 'onHoldPaymentDeadline',
        name: 'Confirmation Deadline',
        width: 200,
        renderCell: ({ row }) => appDisplayDateFormat.default.withJstDateTime(row.onHoldPaymentDeadline),
      },
      {
        key: 'checkinDate',
        name: <Trans>agentBookingListPage_CheckinDate</Trans>,
        width: 140,
        renderCell: ({ row }) => appDisplayDateFormat.default.dateOnly(row.checkinDate),
      },
      {
        key: 'checkoutDate',
        name: <Trans>agentBookingListPage_CheckoutDate</Trans>,
        width: 150,
        renderCell: ({ row }) => appDisplayDateFormat.default.dateOnly(row.checkoutDate),
      },
      {
        key: 'totalPrice',
        name: <Trans>agentBookingListPage_TotalPrice</Trans>,
        width: 140,
        renderCell: ({ row }) => <NTACurrencyFormat className="fw-500" price={row.sellingPrice} />,
      },

      {
        key: 'leadPassenger',
        name: <Trans>agentBookingListPage_LeadPassenger</Trans>,
        width: 200,
        renderCell: ({ row }) => (
          <span className="text-truncate" title={row.leadPassenger}>
            {row.leadPassenger}
          </span>
        ),
      },
      {
        key: 'id',
        name: <Trans>base.Action</Trans>,
        cellClass: 'fix-right',
        headerCellClass: 'fix-right',
        width: 200,
        maxWidth: 200,
        renderCell: ({ row }) => (
          <div className="d-flex items-center">
            {bookingService.canConfirmOnHoldBooking(row) ? (
              <Link to={AppUrls.__createOnHoldPaymentUrl(row.id || '')} className="button px-5 py-1 text-14 text-white fw-500 bg-blue-1">
                <FontAwesomeIcon icon={faCheck} className="text-12 mr-5" />
                {t('base.Confirm')}
              </Link>
            ) : null}
            {bookingService.canCancelOnHoldBooking(row) ? (
              <button
                onClick={() => {
                  cancelBooking(row);
                }}
                className="button px-5 py-1  text-14 fw-500 bg-red-2 text-white ml-5"
              >
                <FontAwesomeIcon icon={faCancel} className="text-12 mr-5" /> {t('base.Cancel')}
              </button>
            ) : null}
          </div>
        ),
      },
    ] as NTADataGridColumnProps<BookingPagingResponse>[];
  }, []);

  return (
    <div>
      {bookingCancelled?.id ? (
        <CancelBookingModal
          bookingId={bookingCancelled.id}
          onCloseModal={reload => {
            if (reload) {
              getData();
            }
            setBookingCancelled(undefined);
          }}
        />
      ) : null}

      <div className="row y-gap-20 justify-between items-end pb-10 lg:pb-40 md:pb-32">
        <div className="col-12">
          <h1 className="text-30 lh-14 fw-600">{t('agentOnHoldBookingListPage_PageTitle')}</h1>
          <div className="text-15 text-light-1">{t('agentOnHoldBookingListPage_PageSubTitle')}</div>
        </div>
      </div>

      <SearchForm onSearch={onSearch} />

      <div className="py-30 px-30 rounded-4 bg-white shadow-3">
        <Spin loading={loading}>
          <NtaDataGrid<BookingPagingResponse>
            key={'id'}
            columns={columns}
            rows={bookingResponse?.data || []}
            columnsKeyConfig="/agent-booking-list-page"
            totalRecord={bookingResponse?.total}
            pageIndex={bookingResponse?.pageIndex}
            pageSize={bookingResponse?.pageSize}
            onPageChange={onPageChange}
            renderers={{ noRowsFallback: <EmptyRowsRenderer /> }}
          />
        </Spin>
      </div>
    </div>
  );
}
